import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { merge, EMPTY, Observable, switchMap, tap, timer, catchError } from 'rxjs';
import { WA_WINDOW } from '@ng-web-apis/common';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { UserStateService } from '@core/services/authApiService/user-state-service.service';
import { LATE_WARNING_TIME, PopupTypes, TimerTypes } from '@core/consts/popups';
import { PopupService } from '@shared/services/popup.service';
import { SVG } from '@core/consts/SVGs';
import { SignInModalService } from '@shared/services/sign-in-modal.service';
import { APPLICATION_ROUTES } from '@core/consts/routes';
import { Timer } from '@core/models/timer.model';

import { SessionExpirationService } from '../../services/session-expiration.service';

@Component({
  selector: 'mdcr-session-expiration',
  imports: [AngularSvgIconModule],
  templateUrl: './session-expiration.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionExpirationComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly sessionExpirationService = inject(SessionExpirationService);
  private readonly signInModalService = inject(SignInModalService);
  private readonly popupService = inject(PopupService);
  private readonly userStateService = inject(UserStateService);
  private readonly window = inject(WA_WINDOW);
  private readonly destroyRef = inject(DestroyRef);

  public readonly SVG = SVG;

  public ngOnInit(): void {
    this.sessionExpirationService.startTokenExpirationTimer();
    this.sessionExpirationService.startFullSession();
    this.startSessionTimer();
    this.startFullSessionTimer();
    this.startInactivityTracking();
  }

  private startInactivityTracking(): void {
    this.sessionExpirationService
      .trackUserInactivity()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.signInModalService.openSignInModal();
        this.onCancelSession();
      });
  }

  private startSessionTimer(): void {
    this.sessionExpirationService
      .getSessionTimer()
      .pipe(
        switchMap((delay) => (delay ? timer(delay) : EMPTY)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.onExtendSession());
  }

  private startFullSessionTimer(): void {
    this.sessionExpirationService
      .getfullSessionTimers()
      .pipe(
        switchMap((timers) =>
          merge(
            ...timers
              .filter((timerItem) => Boolean(timerItem.delay))
              .map((timerItem) => this.handleFullSessionTimer(timerItem)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private handleFullSessionTimer(timerItem: Timer): Observable<number> {
    return timer(timerItem.delay).pipe(
      tap(() => {
        switch (timerItem.type) {
          case TimerTypes.TIME_TO_EARLY_WARNING:
            this.popupService.openPopup(PopupTypes.EARLY_WARNING);
            break;

          case TimerTypes.TIME_TO_LATE_WARNING:
            this.popupService.closePopup();
            this.popupService.openPopup(PopupTypes.LATE_WARNING);
            this.popupService.setTimer(LATE_WARNING_TIME);
            break;

          case TimerTypes.TIME_TO_EXPIRE:
            this.onCancelSession();
            this.router.navigate([APPLICATION_ROUTES.signIn]);
            break;
        }
      }),
    );
  }

  private onExtendSession(): void {
    this.sessionExpirationService.stopTokenExpirationTimer();

    this.userStateService
      .extendSession()
      .pipe(
        tap(() => this.sessionExpirationService.startTokenExpirationTimer()),
        catchError(() => {
          this.onCancelSession();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private onCancelSession(): void {
    this.sessionExpirationService.clearTimers();

    this.userStateService
      .logout()
      .pipe(
        catchError(() => {
          this.userStateService.clearSession();
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
